import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useLazyQuery } from "@apollo/client";

import {
  Device,
  GetDeviceByIdQuery,
  GetDeviceByIdQueryVariables,
  GetServicesQuery,
  GetServicesQueryVariables,
  SelectionKey,
  Service,
} from "../../../API";
import {
  IDeviceMake,
  getDeviceMakeByModel,
} from "../../../common/data/deviceMakes";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { skuCatalog } from "../../../common/models/enums";
import {
  GET_DEVICE_BY_ID,
  GET_SERVICES,
} from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import { useSelectedCustomerId } from "../../../common/variables/selectedCustomer";
import { useListenToDeviceDataChange } from "../../deployment-health/hooks/useListenToDeviceDataChange";
import { useListenToNodeOnlineStatusChange } from "../../deployment-health/hooks/useListenToNodeHealthDataChange";
import {
  IFireSmokeModel,
  IGasLeakModel,
  IGateGuardModel,
  IHardHatModel,
  ILiquidLeakModel,
  ITankLevelModel,
  ServiceTypeEnum,
} from "../../model-manager/variables/modelManager";
import { ALLOWED_MODELS_BY_TYPE } from "../../system-settings/tabs/skus/variables/skuData";
import {
  deviceDataVariable,
  deviceFormVariables,
  useDeviceDataState,
} from "../variables/devices";
import { IDeviceTableRow, IModel } from "./useDeviceTableRows";

export interface IJsonData {
  [key: string]:
    | IGasLeakModel
    | ILiquidLeakModel
    | IFireSmokeModel
    | IHardHatModel
    | ITankLevelModel
    | IGateGuardModel
    | string
    | boolean;
}

const useGetDeviceById = () => {
  const { deviceId = "" } = useParams();

  const selectedCustomerIdInDropdown = useSelectedCustomerId();

  const customerIdFromClaims = useCustomerIdGuard();

  const memoizedCustomerId = useMemo(() => {
    return selectedCustomerIdInDropdown || customerIdFromClaims;
  }, [customerIdFromClaims, selectedCustomerIdInDropdown]);

  const { getCachedLocation } = useLocationFromCache();

  const deviceVariable = useDeviceDataState();

  const [getServices] = useLazyQuery<
    GetServicesQuery,
    GetServicesQueryVariables
  >(GET_SERVICES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  // Use ref to stabilize getServices function reference
  const getServicesRef = useRef(getServices);

  getServicesRef.current = getServices;

  const [serviceData, setServiceData] = useState<Service[]>([]);
  const [getServicesLoading, setGetServicesLoading] = useState(false);

  /**
   * Fetches all services for a location with pagination support.
   * Returns all services for the location - filtering by device is done separately.
   */
  const fetchServicesByLocationFunc = useCallback(
    async (
      locationId: string,
      nextToken?: string,
      accumulator: Service[] = []
    ): Promise<Service[]> => {
      try {
        const response = await getServicesRef.current({
          variables: {
            locationId,
            nextToken,
            modelAlertSettingsSelectionKey: SelectionKey.keys,
          },
        });

        const pageItems = (response?.data?.getServices?.items ??
          []) as Service[];
        const combined = [...accumulator, ...pageItems];

        const token = response?.data?.getServices?.nextToken;

        if (token) {
          return await fetchServicesByLocationFunc(locationId, token, combined);
        }

        if (response.error) {
          errorNotification(response.error.message);
        }

        return combined;
      } catch (e) {
        console.error(e);
        const message = e instanceof Error ? e.message : "Unknown error";

        errorNotification(message);

        return accumulator;
      }
    },
    []
  );

  const { data: deviceHealthData, isOnline } = useListenToDeviceDataChange();

  const { data: nodeData } = useListenToNodeOnlineStatusChange();

  const [getDevice, { data, loading: getDeviceLoading }] = useLazyQuery<
    GetDeviceByIdQuery,
    GetDeviceByIdQueryVariables
  >(GET_DEVICE_BY_ID, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    returnPartialData: true,
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (
      nodeData?.listenToNodeOnlineStatus?.nodeId?.replaceAll("_", "#") ===
        data?.getDeviceById?.nodeId &&
      nodeData?.listenToNodeOnlineStatus?.isOnline === false &&
      deviceVariable
    ) {
      deviceDataVariable({ ...deviceVariable, isOnline: false });
    }
  }, [nodeData]);

  useEffect(() => {
    const isDeviceDataFromSubscription = deviceHealthData?.listenToDeviceData;

    if (!isDeviceDataFromSubscription || !deviceVariable) return;

    if (isDeviceDataFromSubscription.id === deviceVariable.rowId) {
      deviceDataVariable({ ...deviceVariable, isOnline: isOnline });
    }
  }, [deviceHealthData]);

  const fetchDevice = useCallback(async () => {
    if (!memoizedCustomerId || !deviceId) {
      return;
    }

    try {
      const deviceResponse = await getDevice({
        variables: {
          customerId: memoizedCustomerId,
          deviceId,
        },
      });

      const device = deviceResponse?.data?.getDeviceById;

      if (!device) {
        return;
      }

      updateDeviceFormState(device);

      const locationCache = getCachedLocation(device.locationId as string);

      setGetServicesLoading(true);

      const allServices = await fetchServicesByLocationFunc(
        device.locationId as string
      );

      // Filter services to only those belonging to this device
      const deviceServices = filterModelsForDevice(allServices, device);

      setServiceData(deviceServices);

      const deviceMake = device.makeModelId
        ? getDeviceMakeByModel(device.makeModelId)
        : undefined;

      const result = buildDeviceTableRow(
        device,
        locationCache?.name ?? "",
        deviceServices,
        deviceMake
      );

      updateDeviceFormWithExtendedInfo(device, deviceMake);

      setGetServicesLoading(false);

      return result;
    } catch (error) {
      console.error(error);

      setGetServicesLoading(false);

      errorNotification();
    }
  }, [memoizedCustomerId, deviceId]);

  const updateDeviceFormState = useCallback((device: Device) => {
    deviceFormVariables({
      isDirty: false,
      cameraIpAddress: device.cameraIpAddress ?? "",
      deviceData: device.deviceData,
      deviceName: device.name,
      deviceType: device.makeModelId,
      location: {
        value: device.locationId ?? "",
        title: device.locationName ?? "",
      },
      node: {
        id: device.nodeId ?? "",
        name: device.nodeName ?? "",
      },
      id: device.id,
      skuNumber: device.skuNumber ?? null,
      configurationName: device.configurationName ?? null,
      serialNumber: device.serialNumber ?? null,
      componentSerialNumber: device.componentSerialNumber ?? null,
      initialPanTiltType: device.deviceData?.panTiltType ?? null,
    });
  }, []);

  const filterModelsForDevice = useCallback(
    (allModels: Service[], device: Device) => {
      return allModels.filter(service => {
        // Legacy service item's deviceId field has no C# prefix e.g. DE#<>;
        const doesLegacyIdsMatch = service?.deviceId === device?.id;
        // Service item's deviceId field has C# prefix e.g. C#<>DE#<>; must remove before comparison
        const doesIdsMatch = service?.deviceId?.split("DE#")[1]
          ? `DE#${service.deviceId.split("DE#")[1]}` === device?.id
          : false;

        return doesLegacyIdsMatch || doesIdsMatch;
      });
    },
    []
  );

  const buildDeviceTableRow = useCallback(
    (
      device: Device,
      locationName: string,
      models: Service[],
      deviceMakeResponse?: IDeviceMake
    ): IDeviceTableRow => {
      // Determine modelsToRun: SKU-based devices use skuCatalog, legacy devices use DeviceMakes
      const getModelsToRun = (): string[] => {
        const skuNumber = device?.skuNumber;
        const makeModelId = device?.makeModelId;

        // SKU-onboarded device with valid SKU and subComponents
        if (skuNumber && skuCatalog[skuNumber]?.subComponents?.length > 0) {
          const component = skuCatalog[skuNumber].subComponents.find(
            sub => sub.makeModelId === makeModelId
          );

          const legacyDeviceMake = getDeviceMakeByModel(makeModelId);

          if (legacyDeviceMake) {
            // old legacy devices have a makeModel that we lookup in deviceMakes.json for modelsToRun
            return legacyDeviceMake.modelsToRun ?? [];
          } else if (component) {
            // new SKU system device has its own lookup in SKUS.json for modelsToRUn
            return ALLOWED_MODELS_BY_TYPE[component.type] ?? [];
          } else {
            return ALLOWED_MODELS_BY_TYPE["LEGACY"] ?? [];
          }
        }

        return [];
      };

      return {
        rowId: device.id as string,
        name: device.name as string,
        healthData: device.healthData,
        location: {
          id: device.locationId as string,
          name: locationName,
        },
        node: {
          id: (() => {
            const rawNodeId = device.nodeId ?? "";
            const locationId = device.locationId ?? "";

            return rawNodeId &&
              !rawNodeId.includes("#L#") &&
              rawNodeId.includes("#N#") &&
              locationId
              ? `${locationId}#N#${rawNodeId.split("#N#")[1]}`
              : rawNodeId;
          })(),
          name: device.nodeName as string,
        },
        makeModelId: device?.makeModelId as string,
        models:
          models?.map(
            (service): IModel => ({
              serviceName: (service?.serviceType as ServiceTypeEnum) ?? "",
              modelAlertSettingsId: service?.modelAlertSettingsId ?? "",
              modelAlertSettings: service?.modelAlertSettings ?? undefined,
              serviceId: service?.id ?? "",
              nodeId: service?.nodeId ?? "",
              isRunning: service?.isRunning ?? false,
              configuration: service?.configuration ?? "",
            })
          ) ?? [],
        modelsToRun: getModelsToRun(),
        rtspTemplate: deviceMakeResponse?.rtspTemplate ?? "",
        deviceData: device.deviceData,
        currentPosition: device.currentPosition,
        isOnline: device.healthData.isOnline,
        cameraIpAddress: device?.cameraIpAddress as string,
        skuNumber: device?.skuNumber,
        configurationName: device?.configurationName,
        serialNumber: device?.serialNumber,
        componentSerialNumber: device?.componentSerialNumber,
      };
    },
    []
  );

  const updateDeviceFormWithExtendedInfo = useCallback(
    (device: Device, deviceMakeResponse?: IDeviceMake) => {
      const deviceMakePanTiltValue = deviceMakeResponse?.hasPanTilt;
      const deviceMakeHasZoomValue = deviceMakeResponse?.hasZoom ?? false;

      deviceFormVariables({
        isDirty: false,
        cameraIpAddress: device?.cameraIpAddress ?? "",
        deviceData: device.deviceData,
        deviceName: device.name ?? "",
        deviceType: device.makeModelId ?? "",
        location: {
          value: device.locationId ?? "",
          title: device.locationName ?? "",
        },
        node: {
          id: device.nodeId ?? "",
          name: device.nodeName ?? "",
        },
        rtspTemplate: deviceMakeResponse?.rtspTemplate ?? "",
        defaultUserPwd: deviceMakeResponse?.defaultUserPwd ?? "",
        deviceMakePanTiltValue,
        deviceMakeHasZoomValue,
        id: device.id,
        skuNumber: device.skuNumber ?? null,
        configurationName: device.configurationName ?? null,
        serialNumber: device.serialNumber ?? null,
        componentSerialNumber: device.componentSerialNumber ?? null,
        initialPanTiltType: device.deviceData?.panTiltType ?? null,
      });
    },
    []
  );

  return {
    data,
    fetchDevice,
    loading: getDeviceLoading,
    getServicesLoading,
    isServiceLoaded: !!serviceData,
  };
};

export default useGetDeviceById;
